import { consume } from "@lit/context";
import { Task } from "@lit/task";
import { AbsenceStatus, DASHBOARD_CONFIG, dateAdd, GLOBAL_CONSTANTS } from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { enabledFeatures, type EnabledFeatures } from "../../../../contexts/features-context";
import { app } from "../../../../init";
import { shared } from "../../../../styles";
import { getFixedOrCalculatedCommonWorkDays } from "../../utils/common-workdays";
import "../employee-absence-list";
import type { EmployeeAbsenceListItem } from "../employee-absence-list";
import "./base-tile";

@customElement("ptc-dashboard-tile-vacation")
export class DashboardTileVacation extends LitElement {
    @property({ attribute: false })
    size: { inline: number; block: number } = app.settings.dashboard.tiles.vacationRequests.size;

    @state()
    from: DateString = new Date().toISOString() as DateString;

    @state()
    to: DateString = dateAdd(new Date().toISOString() as DateString, {
        months: GLOBAL_CONSTANTS.VACATION_FUTURE_LIMIT_MONTHS,
    }) as DateString;

    @consume({ context: enabledFeatures, subscribe: true })
    features: EnabledFeatures;

    private _absenceTask = new Task(this, {
        task: async ([api]) => {
            const absences = await api
                .getAbsences({
                    from: this.from,
                    to: this.to,
                    status: [AbsenceStatus.Requested],
                })
                .then((absences) =>
                    absences.filter(
                        (absence) => absence.status === AbsenceStatus.Requested && absence.type.includes("vacation")
                    )
                );

            const groupedAbsences = Object.groupBy(absences, (absence) => absence.employeeId);

            const employeeAbsenceList: EmployeeAbsenceListItem[] = (
                await Promise.all(
                    Object.entries(groupedAbsences).map(async ([employeeId, absences = []]) => {
                        const employee = app.getEmployee(Number(employeeId));
                        if (!employee) {
                            return null;
                        }

                        const commonWorkDays = await getFixedOrCalculatedCommonWorkDays(app.api, employee);

                        return {
                            employee,
                            absences,
                            commonWorkDays,
                        };
                    })
                )
            ).filter((entry) => entry !== null && entry.employee !== null) as EmployeeAbsenceListItem[]; // @TODO: remove type cast when removing beta flag

            return employeeAbsenceList;
        },

        args: () => [app.api, this.from, this.to] as const, // @TODO: is app.api needed?
        autoRun: true,
    });

    static styles = [
        shared,
        css`
            :host {
                display: contents;
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--blue)"
                label=${DASHBOARD_CONFIG.tiles.vacationRequests.label}
                .subLabel=${"Alle"}
                icon="icon.time-entry.vacation"
                .size=${this.size}
                tileType="vacationRequests"
                @tile-size-change=${(e: CustomEvent) => {
                    this.size = e.detail.size;
                }}
            >
                ${this._absenceTask.render({
                    initial: () => html``,
                    pending: () =>
                        html`<ptc-ui-spinner
                            class="double-padded place-self-center"
                            active
                            style="font-size: 1rem"
                        ></ptc-ui-spinner>`,
                    error: () =>
                        html`<div class="double-padded text-centering">
                            Urlaubsanträge konnten nicht geladen werden!
                        </div>`,
                    complete: (absences) => {
                        return html`<ptc-employee-absence-list .employeeAbsences=${absences}>
                            <div slot="empty-message" class="double-padded text-centering subtle place-self-center">
                                <span class="emoji huger">🏝️</span>
                                <p>Jeder mit Urlaub versorgt</p>
                            </div>
                        </ptc-employee-absence-list>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-vacation": DashboardTileVacation;
    }
}
